<template>
  <div class="invoice-audit-tabs p-2 border border-top-0 rounded-bottom">
    <div class="invoice-audit-tabs__nav" role="tablist">
      <button
        v-for="tab in tabs"
        :id="tabId(tab.key)"
        :key="tab.key"
        type="button"
        class="invoice-audit-tabs__button"
        :class="{ 'invoice-audit-tabs__button--active': activeTab === tab.key }"
        role="tab"
        :aria-selected="activeTab === tab.key ? 'true' : 'false'"
        :aria-controls="panelId(tab.key)"
        @click="activeTab = tab.key"
      >
        {{ $t(tab.label) }}
      </button>
    </div>
    <div
      v-for="tab in tabs"
      v-show="activeTab === tab.key"
      :id="panelId(tab.key)"
      :key="tab.key"
      class="invoice-audit-tabs__panel"
      role="tabpanel"
      :aria-labelledby="tabId(tab.key)"
    >
      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div class="d-flex align-items-center flex-wrap gap-2">
          <h6 class="mb-0 font-weight-bold">{{ $t(tab.label) }}</h6>
          <div v-if="invoice[tab.payload]" class="endpoint-badge">
            <span class="badge-method">POST</span>
            <code>{{ tab.url }}</code>
          </div>
        </div>
      </div>
      <div class="json-viewer-container">
        <button
          v-if="invoice[tab.payload]"
          type="button"
          class="btn btn-sm btn-outline-light json-copy-button"
          :title="$t('common.copy')"
          :aria-label="$t('common.copy')"
          @click="$emit('copy', invoice[tab.payload])"
        >
          <i class="fa fa-copy" aria-hidden="true"></i>
        </button>
        <pre class="json-viewer">{{ formatAuditJson(invoice[tab.payload]) }}</pre>
      </div>
      <div v-if="invoice[tab.response]" class="mt-2">
        <div class="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
          <h6 class="mb-0 font-weight-bold small text-muted">{{ tab.responseLabel }}</h6>
          <div class="endpoint-badge"><code>{{ tab.url }}</code></div>
        </div>
        <div class="json-viewer-container">
          <button
            type="button"
            class="btn btn-sm btn-outline-light json-copy-button"
            :title="$t('common.copy')"
            :aria-label="$t('common.copy')"
            @click="$emit('copy', invoice[tab.response])"
          >
            <i class="fa fa-copy" aria-hidden="true"></i>
          </button>
          <pre class="json-viewer">{{ formatJson(invoice[tab.response]) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InvoiceAuditTabs",
  props: {
    invoice: { type: Object, required: true },
    iesiriUrl: { type: String, required: true },
    intrariUrl: { type: String, required: true },
    formatAuditJson: { type: Function, required: true },
    formatJson: { type: Function, required: true },
  },
  data() {
    return { activeTab: "outgoing" };
  },
  computed: {
    tabs() {
      return [
        {
          key: "outgoing",
          label: "tools.erp_integration.consum_erp_summary_outgoing_section",
          payload: "iesiri_payload",
          response: "iesiri_response",
          responseLabel: this.$t(
            this.invoice.intrari_payload || this.invoice.intrari_response
              ? "tools.erp_integration.consum_invoice_erp_response_outgoing"
              : "tools.erp_integration.consum_invoice_erp_response"
          ),
          url: this.iesiriUrl,
        },
        {
          key: "incoming",
          label: "tools.erp_integration.consum_erp_summary_incoming_section",
          payload: "intrari_payload",
          response: "intrari_response",
          responseLabel: this.$t("tools.erp_integration.consum_invoice_erp_response_incoming"),
          url: this.intrariUrl,
        },
      ];
    },
  },
  methods: {
    tabId(key) {
      return `invoice-audit-tab-${this._uid}-${key}`;
    },
    panelId(key) {
      return `invoice-audit-panel-${this._uid}-${key}`;
    },
  },
};
</script>

<style scoped>
.invoice-audit-tabs__nav {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
  border-bottom: 1px solid #dfe6ef;
}

.invoice-audit-tabs__button {
  padding: 8px 14px;
  border: 1px solid #dfe6ef;
  border-radius: 8px 8px 0 0;
  background: #f4f7fb;
  color: #3b506c;
  font-weight: 600;
  cursor: pointer;
}

.invoice-audit-tabs__button:hover {
  background: #e9f0f9;
}

.invoice-audit-tabs__button--active {
  background: #183052;
  border-color: #183052;
  color: #fff;
}

.invoice-audit-tabs__button:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.invoice-audit-tabs__panel {
  min-width: 0;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.json-viewer {
  max-height: 400px;
  overflow: auto;
  margin: 0;
  padding: 42px 14px 14px;
  border-radius: 8px;
  background: #1e293b;
  color: #e2e8f0;
  font-family: monospace;
  font-size: 12px;
}

.json-viewer-container {
  position: relative;
}

.json-copy-button {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  padding: 4px 7px;
  line-height: 1;
}

.endpoint-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 7px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #f1f5f9;
  font-size: 11px;
}

.endpoint-badge .badge-method {
  padding: 1px 5px;
  border-radius: 3px;
  background: #2563eb;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.endpoint-badge code {
  color: #0f172a;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 11px;
  word-break: break-all;
}
</style>
